// src/prototype/components/sep/SEPSectionExecutionSequencing.tsx
// Component 5: execution_roadmap

import React from 'react';
import type { StrategicExecutionPlanSections } from '../../services/strategicExecutionPlanService';
import type {
  MappedExecutionRoadmapInitiative,
  MappedExecutionRoadmapMilestone,
} from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionExecutionSequencingProps {
  clientName: string;
  horizonMonths: number;
  initiatives: MappedExecutionRoadmapInitiative[];
  sequencingInsights?: NonNullable<StrategicExecutionPlanSections['execution_roadmap']>['sequencing_insights'];
  /** Shared with Executive Summary when sequencing_insights is absent. */
  quickWinCount?: number;
}

const COLORS = ['#18365E', '#FF6E4C', '#2563EB'];

/** Left edge of an inclusive month slot on the timeline (month 1 → 0%). */
const monthStartPercent = (month: number, timelineMonths: number) =>
  Math.min(100, Math.max(0, ((month - 1) / timelineMonths) * 100));

/** Center of an inclusive month slot — used for milestone markers. */
const monthCenterPercent = (month: number, timelineMonths: number) =>
  Math.min(100, Math.max(0, ((month - 0.5) / timelineMonths) * 100));

/** Width spanning inclusive months [startMonth, endMonth]. */
const monthSpanPercent = (startMonth: number, endMonth: number, timelineMonths: number) =>
  Math.max(0.5, ((endMonth - startMonth + 1) / timelineMonths) * 100);

type MilestoneMarker = {
  monthsFromStart: number;
  milestones: MappedExecutionRoadmapMilestone[];
  leftPercent: number;
};

const groupMilestoneMarkers = (
  milestones: MappedExecutionRoadmapMilestone[],
  timelineMonths: number,
): MilestoneMarker[] => {
  const byMonth = new Map<number, MappedExecutionRoadmapMilestone[]>();
  for (const milestone of milestones) {
    const bucket = byMonth.get(milestone.monthsFromStart) ?? [];
    bucket.push(milestone);
    byMonth.set(milestone.monthsFromStart, bucket);
  }

  const monthWidthPercent = 100 / timelineMonths;
  const minGapPercent = monthWidthPercent * 0.72;

  const sortedMonths = Array.from(byMonth.keys()).sort((a, b) => a - b);
  const adjustedPercents = sortedMonths.map(month => monthCenterPercent(month, timelineMonths));

  for (let i = 1; i < adjustedPercents.length; i += 1) {
    adjustedPercents[i] = Math.max(adjustedPercents[i], adjustedPercents[i - 1] + minGapPercent);
  }

  const maxCenter = 100 - monthWidthPercent * 0.25;
  const minCenter = monthWidthPercent * 0.25;
  for (let i = adjustedPercents.length - 1; i >= 0; i -= 1) {
    adjustedPercents[i] = Math.min(adjustedPercents[i], maxCenter);
    if (i < adjustedPercents.length - 1) {
      adjustedPercents[i] = Math.min(adjustedPercents[i], adjustedPercents[i + 1] - minGapPercent);
    }
    adjustedPercents[i] = Math.max(adjustedPercents[i], minCenter);
  }

  return sortedMonths.map((monthsFromStart, index) => ({
    monthsFromStart,
    milestones: byMonth.get(monthsFromStart) ?? [],
    leftPercent: adjustedPercents[index],
  }));
};

const PhaseLabel: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div className="flex items-center gap-1.5">
    <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
    <span className="text-[10px] text-[#717182]">{label}</span>
  </div>
);

export const SEPSectionExecutionSequencing: React.FC<SEPSectionExecutionSequencingProps> = ({
  clientName, horizonMonths, initiatives, sequencingInsights, quickWinCount,
}) => {
  // horizonMonths is already the portfolio delivery span from the mapper (§1 + §5).
  const timelineMonths = Math.max(horizonMonths, 1);
  const resolvedQuickWinCount = sequencingInsights?.quick_win_count ?? quickWinCount;

  const quarterCount = Math.ceil(timelineMonths / 3);
  const monthColumns = Array.from({ length: timelineMonths }, (_, i) => i + 1);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 5</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Execution Sequencing</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">{timelineMonths}-month delivery timeline across all portfolio initiatives</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The sequencing plan below shows the recommended delivery timeline for <strong>{clientName}</strong>'s initiative portfolio across a <strong>{timelineMonths}-month</strong> horizon. Each initiative is phased to manage organizational capacity while maximizing early value delivery.
      </p>

      <div className="overflow-x-auto">
        <div style={{ minWidth: `${Math.max(640, timelineMonths * 36)}px` }}>
          <div className="flex mb-2">
            <div className="w-[180px] shrink-0" />
            <div
              className="flex-1 grid gap-0"
              style={{ gridTemplateColumns: `repeat(${timelineMonths}, minmax(0, 1fr))` }}
            >
              {monthColumns.map(month => (
                <div
                  key={month}
                  className={`text-center text-[9px] font-semibold py-1 ${(month - 1) % 3 === 0 ? 'text-[#18365E]' : 'text-[#717182]/60'}`}
                >
                  {(month - 1) % 3 === 0 ? `Q${Math.ceil(month / 3)}` : `M${month}`}
                </div>
              ))}
            </div>
          </div>

          <div className="flex mb-3">
            <div className="w-[180px] shrink-0" />
            <div
              className="flex-1 grid gap-0 border border-[#e5e7eb] rounded-lg overflow-hidden"
              style={{ gridTemplateColumns: `repeat(${quarterCount}, minmax(0, 1fr))` }}
            >
              {Array.from({ length: quarterCount }, (_, i) => (
                <div
                  key={i}
                  className="text-center text-[9px] font-bold text-[#18365E]/40 py-1 border-r border-[#e5e7eb] last:border-0 bg-[#F5F7FA]"
                >
                  Q{i + 1}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {initiatives.map((initiative, idx) => {
              const color = COLORS[idx % COLORS.length];
              const milestoneMarkers = groupMilestoneMarkers(initiative.milestones, timelineMonths);

              return (
                <div key={initiative.id}>
                  <div className="flex items-stretch gap-0">
                    <div className="w-[180px] shrink-0 pr-3">
                      <p className="text-xs font-semibold text-[#18365E] leading-tight line-clamp-2">{initiative.title}</p>
                      <p className="text-[10px] text-[#717182] mt-0.5">
                        M{initiative.startMonth}–M{initiative.endMonth}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="relative h-10 overflow-hidden rounded-md">
                        <div
                          className="absolute inset-0 grid gap-0"
                          style={{ gridTemplateColumns: `repeat(${timelineMonths}, minmax(0, 1fr))` }}
                        >
                          {monthColumns.map(month => (
                            <div
                              key={month}
                              className={`h-full ${(month - 1) % 3 === 0 ? 'bg-[#F5F7FA]' : 'bg-white'} border-r border-[#e5e7eb]/50 last:border-0`}
                            />
                          ))}
                        </div>

                        <div
                          className="absolute top-1.5 bottom-1.5 rounded-md border border-white/40"
                          style={{
                            left: `${monthStartPercent(initiative.startMonth, timelineMonths)}%`,
                            width: `${monthSpanPercent(initiative.startMonth, initiative.endMonth, timelineMonths)}%`,
                            background: `${color}22`,
                          }}
                        />

                        {initiative.phases.map(phase => (
                          <div
                            key={`${phase.name}-${phase.startMonth}`}
                            className="absolute top-2 bottom-2 rounded flex items-center justify-center text-white text-[8px] font-semibold px-0.5 overflow-hidden"
                            style={{
                              left: `${monthStartPercent(phase.startMonth, timelineMonths)}%`,
                              width: `${monthSpanPercent(phase.startMonth, phase.endMonth, timelineMonths)}%`,
                              background: color,
                              opacity: 0.85,
                            }}
                            title={`${phase.name} (M${phase.startMonth}–M${phase.endMonth})`}
                          >
                            <span className="truncate">{phase.name}</span>
                          </div>
                        ))}
                      </div>

                      {milestoneMarkers.length > 0 && (
                        <div className="relative mt-1 h-3 overflow-hidden">
                          {milestoneMarkers.map(marker => {
                            const title = marker.milestones
                              .map(m => (m.valueNote ? `${m.label}: ${m.valueNote}` : m.label))
                              .join(' · ');

                            return (
                              <div
                                key={`milestone-${marker.monthsFromStart}`}
                                className="absolute top-0 flex flex-col items-center"
                                style={{
                                  left: `${marker.leftPercent}%`,
                                  transform: 'translateX(-50%)',
                                }}
                                title={title}
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-[#FF6E4C] ring-2 ring-white shadow-sm" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {initiative.milestones.length > 0 && (
                    <div className="flex gap-0 mt-1">
                      <div className="w-[180px] shrink-0" />
                      <div className="flex-1 space-y-1">
                        {initiative.milestones.map(milestone => (
                          <div key={`${milestone.label}-note`} className="flex items-start gap-2 text-[10px] text-[#717182]">
                            <span className="font-semibold text-[#18365E] shrink-0">M{milestone.monthsFromStart}</span>
                            <span className="font-medium text-[#18365E]/80">{milestone.label}</span>
                            {milestone.valueNote && <span>— {milestone.valueNote}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex items-center gap-5 flex-wrap">
            {initiatives.map((initiative, idx) => (
              <PhaseLabel
                key={initiative.id}
                label={initiative.title.split(' ').slice(0, 3).join(' ') + '…'}
                color={COLORS[idx % COLORS.length]}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Sequencing Principles</p>
        <div className="space-y-1.5">
          {[
            resolvedQuickWinCount
              ? `${resolvedQuickWinCount} Quick Win initiative${resolvedQuickWinCount !== 1 ? 's' : ''} can launch in the earliest phase to generate organizational confidence.`
              : 'Quick Win initiatives launch in the earliest phase to generate early organizational confidence and measurable ROI.',
            sequencingInsights?.moderate_count
              ? `${sequencingInsights.moderate_count} moderate-effort program${sequencingInsights.moderate_count !== 1 ? 's' : ''} follow structured pilot phases with defined milestones.`
              : 'Moderate-effort programs begin after Q1 infrastructure is established, with pilot phases in Q2.',
            sequencingInsights?.complex_count
              ? `${sequencingInsights.complex_count} complex initiative${sequencingInsights.complex_count !== 1 ? 's' : ''} use phased delivery to reduce commitment risk.`
              : 'High-complexity initiatives use phased delivery to reduce commitment risk and enable course correction.',
            `Portfolio milestones are structured to deliver value across the ${timelineMonths}-month execution horizon.`,
          ].map((note, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-1 h-1 rounded-full bg-[#FF6E4C] mt-1.5 shrink-0" />
              <p className="text-xs text-[#18365E]/75 leading-relaxed">{note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SEPSectionExecutionSequencing;
